import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export type UnpackListItem = {
  id:              string   // list_items.id  (row id — used for toggle/update)
  pantryItemId:    string   // pantry_items.id (used for confirmations insert)
  name:            string
  category:        string | null   // raw display_category (used for smart defaults)
  categoryLabel:   string | null   // titleized version for UI
  confirmedBought: boolean  // list_items.status === 'checked'
  isImpulse:       boolean
}

export type UnpackList = {
  id:          string
  householdId: string
  generatedAt: string
} | null

export function useUnpackList(householdId: string | null): {
  list:    UnpackList
  items:   UnpackListItem[]
  loading: boolean
  error:   string | null
  refresh: () => void
} {
  const [list,    setList]    = useState<UnpackList>(null)
  const [items,   setItems]   = useState<UnpackListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [nonce,   setNonce]   = useState(0)

  useEffect(() => {
    if (!householdId) { setLoading(false); return }

    let cancelled = false
    setLoading(true)
    setError(null)

    ;(async () => {
      // Fetch the most recent uncompleted list for this household.
      const { data: listRow, error: listErr } = await supabase
        .from('shopping_lists')
        .select('id, household_id, generated_at')
        .eq('household_id', householdId)
        .is('completed_at', null)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (cancelled) return

      if (listErr || !listRow) {
        setList(null)
        setItems([])
        setLoading(false)
        if (listErr) {
          console.error('[useUnpackList] list query failed:', listErr)
          setError(listErr.message)
        }
        return
      }

      // Items query. is_impulse_buy is added by the unpack_spend_columns
      // migration; if that migration hasn't been pushed to the live DB yet,
      // the SELECT throws "column ... does not exist" and the whole items
      // load fails. Detect that and retry without the column so unpack still
      // works — impulse rows just render as regular items until the column
      // is available.
      let rows: any[] | null = null
      let rowsErr: any = null
      let hasImpulseColumn = true

      {
        const r = await supabase
          .from('list_items')
          .select(`
            id,
            status,
            is_impulse_buy,
            pantry_items(id, name, display_category)
          `)
          .eq('list_id', listRow.id)
          .neq('status', 'removed')
        rows    = r.data
        rowsErr = r.error
      }

      if (rowsErr && /is_impulse_buy/i.test(rowsErr.message ?? '')) {
        console.warn(
          '[useUnpackList] is_impulse_buy column is missing in your Supabase DB. ' +
          'Apply the migration at supabase/migrations/20260423180000_unpack_spend_columns.sql ' +
          '(via `supabase db push` or the Supabase SQL editor). Falling back without the column.'
        )
        hasImpulseColumn = false
        const r = await supabase
          .from('list_items')
          .select(`
            id,
            status,
            pantry_items(id, name, display_category)
          `)
          .eq('list_id', listRow.id)
          .neq('status', 'removed')
        rows    = r.data
        rowsErr = r.error
      }

      if (cancelled) return

      if (rowsErr) {
        // Items failed for some other reason. Still surface the list so the
        // page can render something other than the "no active trip" empty
        // state — that copy is for the absence of a list, not for a DB error.
        console.error('[useUnpackList] items query failed:', rowsErr)
        setList({
          id:          listRow.id,
          householdId: listRow.household_id,
          generatedAt: listRow.generated_at,
        })
        setItems([])
        setError(rowsErr.message)
        setLoading(false)
        return
      }

      const mapped: UnpackListItem[] = (rows ?? [])
        .filter((r: any) => r.pantry_items)
        .map((r: any) => {
          const pi = r.pantry_items as any
          const raw = pi.display_category as string | null | undefined
          return {
            id:              r.id,
            pantryItemId:    pi.id,
            name:            pi.name,
            category:        raw ?? null,
            categoryLabel:   raw
              ? raw.charAt(0).toUpperCase() + raw.slice(1).replace(/_/g, ' ')
              : null,
            confirmedBought: r.status === 'checked',
            isImpulse:       hasImpulseColumn ? !!r.is_impulse_buy : false,
          }
        })

      setList({
        id:          listRow.id,
        householdId: listRow.household_id,
        generatedAt: listRow.generated_at,
      })
      setItems(mapped)
      setLoading(false)
    })()

    return () => { cancelled = true }
  }, [householdId, nonce])

  return { list, items, loading, error, refresh: () => setNonce(n => n + 1) }
}
